import { useEffect } from 'react'
import { COLORS } from 'consts/colors'
import * as Styled from '../styled'
import { useSelector } from 'react-redux'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { DotsLoading } from 'common-components'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import { projectSelectors } from 'store/features/project/selectors'
import { dispatch } from 'store'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { PROJECT_MEDIA_TYPES } from 'consts'
import VersionHistoryList from '../VersionHistoryList'
import VersionHistoryEmpty from '../VersionHistoryEmpty'

const VersionHistory = ({
  variant = 'overlay'
}: {
  variant?: 'sidebar' | 'overlay'
}) => {
  const activeMedia = useSelector(projectDataSelectors.activeMedia)
  const isGenAiProject = !!useSelector(projectSelectors.meta)?.genAiProjectId
  const aiImageOverlayData = useSelector(aiImageSelectors.aiImageOverlayData)
  const aiVideoGenerationParams = useSelector(aiVideoSelectors.generationParams)
  const isImageType =
    activeMedia?.type === PROJECT_MEDIA_TYPES.aiImage ||
    activeMedia?.type === PROJECT_MEDIA_TYPES.image

  const videoVersionItems = useSelector(aiVideoSelectors.versionHistoryItems)
  const videoIsLoading = useSelector(aiVideoSelectors.isVersionHistoryLoading)
  const videoIsLoaded = useSelector(aiVideoSelectors.isVersionHistoryLoaded)

  const imageVersionItems = useSelector(aiImageSelectors.versionHistoryItems)
  const imageIsLoading = useSelector(aiImageSelectors.isVersionHistoryLoading)
  const imageIsLoaded = useSelector(aiImageSelectors.isVersionHistoryLoaded)

  const allVersionItems = isImageType ? imageVersionItems : videoVersionItems
  const isLoading = isImageType ? imageIsLoading : videoIsLoading
  const isLoaded = isImageType ? imageIsLoaded : videoIsLoaded

  // Safety net so a version never lists itself. The video `/versions` endpoint
  // already excludes the queried id (we query by the active version below), so
  // this is normally a no-op; it also guards the image path, whose endpoint
  // containment we don't rely on.
  const versionItems = allVersionItems.filter(
    item => String(item.id) !== String(activeMedia?.assetId)
  )

  const imageFetchId = isGenAiProject
    ? aiVideoGenerationParams?.parentProjectId || aiImageOverlayData?.id
    : aiImageOverlayData?.id
  // Fetch by the ACTIVE version's own id, not the chain root. The
  // `/videos/{id}/versions` endpoint returns every OTHER version in the chain —
  // the original (root) included — and excludes the queried id. Querying the
  // chain root instead omitted the root itself, so a regenerated scene never
  // showed its first version. Fall back to the chain root only if the active
  // asset id is missing.
  const videoFetchId =
    activeMedia?.assetId || aiVideoGenerationParams?.parentProjectId
  const fetchId = isImageType ? imageFetchId : videoFetchId

  // Fetch once per item entry. Re-entering the tab remounts this component,
  // which re-runs the effect and triggers a fresh API call.
  // When fetchId is non-numeric (e.g. pending regeneration hash after refresh),
  // the thunk will mark history as loaded-empty so the empty state renders.
  useEffect(() => {
    if (isImageType) {
      dispatch(aiImageThunks.getVersionHistory(fetchId ?? ''))
    } else {
      dispatch(aiVideoThunks.getVersionHistory(fetchId ?? ''))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchId, isImageType])

  const hasVersionData = versionItems.length > 0

  return (
    <Styled.HistoryContainer $variant={variant}>
      {isLoading && !hasVersionData && (
        <DotsLoading dotColor={COLORS.primaryDark} size="small" />
      )}

      {!hasVersionData && !isLoading && isLoaded && <VersionHistoryEmpty />}

      {hasVersionData && <VersionHistoryList items={versionItems} />}
    </Styled.HistoryContainer>
  )
}

export default VersionHistory
